import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { ArchWindow } from "./ArchWindow";

export const MobileMenu = ({ open, onClose, links }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="mobile-menu-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-[#120716]/40 backdrop-blur-sm md:hidden"
          />
          <motion.aside
            data-testid="mobile-menu"
            role="dialog"
            aria-label="Site menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-[86vw] max-w-sm bg-white shadow-[-24px_0_80px_rgba(18,7,22,0.2)] flex flex-col md:hidden"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[#120716]/10">
              <div className="flex items-center gap-3">
                <ArchWindow size={22} />
                <div className="leading-none">
                  <span className="font-orbitron font-black text-[#120716] text-base tracking-widest">DIGITAL</span>
                  <span className="block font-orbitron text-[9px] tracking-[0.5em] text-[#E84C4C]">MISSIONS</span>
                </div>
              </div>
              <button data-testid="mobile-menu-close" onClick={onClose} aria-label="Close menu" className="w-10 h-10 rounded-full border border-[#120716]/10 flex items-center justify-center text-[#120716] hover:border-[#E84C4C] hover:text-[#E84C4C] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-6">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={onClose}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-center justify-between py-4 border-b border-[#120716]/8"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="font-orbitron text-[10px] tracking-widest text-[#E84C4C]/70">0{i + 1}</span>
                    <span className="font-orbitron font-bold text-xl tracking-wide text-[#120716] group-hover:text-[#E84C4C] transition-colors">{l.label.toUpperCase()}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#120716]/30 group-hover:text-[#E84C4C] group-hover:translate-x-1 transition-all" />
                </motion.a>
              ))}
            </nav>
            <div className="px-6 pb-8 pt-4 border-t border-[#120716]/10 bg-[#FFF5F2] space-y-3">
              <a href="#apply" onClick={onClose} data-testid="mobile-menu-apply" className="block text-center rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white font-semibold py-3.5 transition-all">Tell Me About Your Church</a>
              <a href="#donate" onClick={onClose} data-testid="mobile-menu-give" className="block text-center rounded-full border border-[#E84C4C]/40 text-[#120716] font-semibold py-3.5 hover:bg-[#E84C4C]/10 transition-all">Give to the Mission</a>
              <p className="pt-3 text-center text-[11px] italic text-[#C13B3B]">"Your word is a lamp to my feet." — Psalm 119:105</p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
